import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { UserCog } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input, { FieldLabel, FieldGroup } from '../ui/Input';
import Select from '../ui/Select';
import Badge from '../ui/Badge';
import { updateAdminUser } from '../../utils/api';
import { roleBadge } from '../../utils/commandCenterHelpers';

export default function EditUserModal({ open, user, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('viewer');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setRole(user.role || 'viewer');
      setActive(user.active !== false);
    }
  }, [user]);

  if (!open || !user) return null;

  async function save() {
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() && name.trim() !== user.name) payload.name = name.trim();
      if (role !== user.role) payload.role = role;
      if (active !== (user.active !== false)) payload.active = active;

      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      const res = await updateAdminUser(user.id, payload);
      toast.success('Account updated');
      onSaved?.(res.data.user);
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  const badge = roleBadge(user.role);

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title="Edit account"
      description={`@${user.username}`}
      icon={UserCog}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FieldGroup>
          <FieldLabel>Full name</FieldLabel>
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Role</FieldLabel>
          <Select value={role} onChange={(e) => setRole(e.target.value)} disabled={saving}>
            <option value="viewer">Viewer — read only</option>
            <option value="admin">Admin — can edit</option>
            <option value="superadmin">Super Admin — full governance</option>
          </Select>
          <p className="text-[11.5px] text-slate-400 mt-1.5">
            Currently <Badge variant={badge.variant} size="sm">{badge.label}</Badge>. Promoting to
            Super Admin grants Command Center access.
          </p>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Account status</FieldLabel>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActive(true)}
              className={`flex-1 h-9 rounded-md border text-[13px] font-medium transition-colors ${
                active
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setActive(false)}
              className={`flex-1 h-9 rounded-md border text-[13px] font-medium transition-colors ${
                !active
                  ? 'bg-rose-50 border-rose-300 text-rose-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              Deactivated
            </button>
          </div>
          <p className="text-[11.5px] text-slate-400 mt-1.5">
            Deactivated accounts cannot sign in but are retained for audit history.
          </p>
        </FieldGroup>
      </div>
    </Modal>
  );
}
